'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import NextLink from 'next/link'
import type { ReactElement } from 'react'
import { useRef } from 'react'
import { useFocusRing, useHover, useLink } from 'react-aria'
import { useLinkClickHandler } from '../_hooks/useLinkClickHandler'
import type { TextLinkProps } from '../Link.types'
import './LinkText.css'
import { textLinkCva } from './linkTextCva'

function LinkTextInner(props: TextLinkProps): ReactElement {
  const ref = useRef<HTMLAnchorElement>(null)
  const handleClick = useLinkClickHandler(props)

  const { isHovered, hoverProps } = useHover({})
  const { isPressed, linkProps } = useLink({}, ref)
  const { isFocusVisible, focusProps } = useFocusRing()
  const {
    href,
    opensInNewTab,
    className,
    underlined,
    bold,
    tint,
    icon,
    children,
    onPress: _onPress,
    intent: _intent,
    trackingData: _trackingData,
    'data-testid': testid = 'link',
    'aria-current': ariaCurrent,
    ...passthrough
  } = props

  const finalClassName = twMerge(
    icon && 'flex flex-row items-center gap-1 leading-light',
    className,
    textLinkCva({ underlined, bold, tint }),
  )

  return (
    <NextLink
      ref={ref}
      {...passthrough}
      {...linkProps}
      {...focusProps}
      {...hoverProps}
      href={href}
      target={opensInNewTab ? '_blank' : undefined}
      data-testid={testid}
      className={finalClassName}
      onClick={handleClick}
      data-hovered={isHovered ? 'true' : undefined}
      data-pressed={isPressed ? 'true' : undefined}
      data-focus-visible={isFocusVisible ? 'true' : undefined}
      aria-current={ariaCurrent}
    >
      {icon}
      {children}
    </NextLink>
  )
}

export function LinkText({ intent, ...props }: TextLinkProps): React.ReactNode {
  const isInWebView = useIsReactNativeWebView()

  // ⚠️ Temporary: hide buttons without intent in the WebView until their specification is ready,
  // to avoid displaying TODO alerts in production
  if (isInWebView && !intent && process.env.NODE_ENV === 'production') {
    return null
  }

  return <LinkTextInner intent={intent} {...props} />
}
